import { useFetch } from '@gluedigital/ruse-fetch-extras'
import { QUERY_BUILDER_URL } from 'src/constants/global'
import { DatasetDataResponse } from './useCatalog'
import { BlueprintNode, DatasetSummary, QueryListItem } from 'src/types/queries'
import { useFetchCb } from '@gluedigital/ruse-fetch-extras'
import type { ColumnDefinition } from 'src/types/tableDataSet'

export interface QueryResponse {
  ok: boolean
  query: QueryListItem
  datasets: DatasetSummary[]
}
export interface QueryColumnsResponse {
  ok: boolean
  columns: string[]
  colSpec: ColumnDefinition[]
}

export interface QueryDataResponse extends DatasetDataResponse {
  lastRun: string
}

export interface ColumnValuesResponse {
  ok: boolean
  values: string[]
}

export const useQueryInfo = (id: string): QueryListItem => {
  const { query } = useFetch<QueryResponse>(QUERY_BUILDER_URL + '/query/' + id)
  return query
}

export const useQuerySources = (
  id: string
): { query: QueryListItem; datasets: DatasetSummary[] } => {
  const { query, datasets } = useFetch<QueryResponse>(
    QUERY_BUILDER_URL + '/query/' + id + '/sources'
  )
  return { query, datasets }
}

export const useQueryData = (id: string): QueryDataResponse =>
  useFetch<QueryDataResponse>(QUERY_BUILDER_URL + '/query/' + id + '/data')

export const useRunQuery = () => {
  const doFetch = useFetchCb()
  const refreshData = useFetchCb()
  const refreshQuery = useFetchCb()

  const runQuery = async (id: string) => {
    return await doFetch(QUERY_BUILDER_URL + '/query/' + id + '/run', {
      method: 'POST'
    })
  }

  const refreshQueryData = async (id: string) => {
    return await refreshData(QUERY_BUILDER_URL + '/query/' + id + '/data')
  }

  const refreshQueryInfo = async (id: string) => {
    return await refreshQuery(QUERY_BUILDER_URL + '/query/' + id)
  }

  return {
    runQuery,
    refreshQueryData,
    refreshQueryInfo
  }
}

export const useQueryCRUD = () => {
  const doCreate = useFetchCb()
  const doUpdate = useFetchCb()
  const doDelete = useFetchCb()

  const createQuery = async (data: {
    title?: string
    blueprint?: BlueprintNode[]
    public?: boolean
  }) => {
    const response = await doCreate(QUERY_BUILDER_URL + '/query', {
      method: 'POST',
      body: data
    })
    return response as QueryResponse
  }

  const updateQuery = async (id: string, payload: Partial<QueryListItem>) => {
    const response = await doUpdate(QUERY_BUILDER_URL + '/query/' + id, {
      method: 'PUT',
      body: payload
    })
    return response as QueryResponse
  }

  const deleteQuery = async (id: string) => {
    await doDelete(QUERY_BUILDER_URL + '/query/' + id, {
      method: 'DELETE'
    })
  }

  return { createQuery, updateQuery, deleteQuery }
}

export const useQueryColumns = (id: string): QueryColumnsResponse =>
  useFetch<QueryColumnsResponse>(`${QUERY_BUILDER_URL}/query/${id}/columns`)

export const useBlueprintColumns = (
  source: BlueprintNode,
  cacheKey?: string
): QueryColumnsResponse => {
  const url = cacheKey
    ? `${QUERY_BUILDER_URL}/query/columns?ck=${encodeURIComponent(cacheKey)}`
    : `${QUERY_BUILDER_URL}/query/columns`

  return useFetch<QueryColumnsResponse>(url, {
    method: 'POST',
    body: source
  })
}

export const useColumnValues = (
  blueprint: BlueprintNode,
  column: string,
  cacheKey?: string
): string[] => {
  const ck = cacheKey ?? `${blueprint.nodeId}:${column}`
  const url = `${QUERY_BUILDER_URL}/query/column-values?ck=${encodeURIComponent(ck)}`

  const res = useFetch<ColumnValuesResponse>(url, {
    method: 'POST',
    body: { blueprint, column }
  })

  return res.values ?? []
}
